import { isDefined, isNotDefined } from '../isEmpty'
import { isDeleted } from '../common'
import { splitArray } from '../array'
import {
  type AccountStatusScope,
  type WorkflowHistoryItem,
  type WorkflowStageData,
  type WorkflowStageId,
} from '../../model'

export const isWorkflowPipelineStage = (stage: Pick<WorkflowStageData, 'order'>): boolean => isDefined(stage.order)

export const comparePipelineStageOrder = <T extends { order: number; name: string }>(a: T, b: T): number => {
  if (a.order === b.order) return a.name.localeCompare(b.name)
  return a.order - b.order
}

type BackfilledStageHistory = {
  pipeline: WorkflowHistoryItem[]
  nonPipeline: WorkflowHistoryItem[]
}

export function backfillPipelineStages(
  history: WorkflowHistoryItem[],
  stages: Pick<WorkflowStageData, 'stageId' | 'order'>[]
): BackfilledStageHistory {
  const stageOrderMap = new Map<WorkflowStageId, number | undefined>(stages.map((s) => [s.stageId, s.order]))
  const historyStageIds = new Set(history.map((item) => item.stageId))

  const { yes: pipelineHistory, no: nonPipeline } = splitArray(history, (item) =>
    isDefined(stageOrderMap.get(item.stageId))
  )

  const pipelineStages = stages.filter((s) => isDefined(s.order))
  const byTimestampAsc = [...pipelineHistory].sort((a, b) => a.timestamp - b.timestamp)

  const findEarliestAfter = (order: number): WorkflowHistoryItem | undefined =>
    byTimestampAsc.find((item) => (stageOrderMap.get(item.stageId) ?? -1) > order)

  const missingPipelineStages = pipelineStages
    .filter((s) => !historyStageIds.has(s.stageId))
    .flatMap((s) => {
      const source = findEarliestAfter(s.order ?? -1)
      return source ? [{ stageId: s.stageId, userId: source.userId, timestamp: source.timestamp }] : []
    })

  const pipeline = [...pipelineHistory, ...missingPipelineStages].sort(
    (a, b) => (stageOrderMap.get(a.stageId) ?? -1) - (stageOrderMap.get(b.stageId) ?? -1)
  )

  return { pipeline, nonPipeline }
}

type WorkflowStages = {
  pipelineStages: WorkflowStageData[]
  otherStages: WorkflowStageData[]
  archivedStages: WorkflowStageData[]
}
export const toWorkflowStages = (stages: WorkflowStageData[]): WorkflowStages => {
  const { yes: archivedStages, no: activeStages } = splitArray(stages, isDeleted)
  const { yes: pipelineStages, no: otherStages } = splitArray(activeStages, isWorkflowPipelineStage)
  return {
    pipelineStages: pipelineStages.sort((a, b) => {
      if (isNotDefined(a.order) || isNotDefined(b.order)) throw new Error('Pipeline stage must have an order')
      if (a.order === b.order) return a.stageId.localeCompare(b.stageId)
      return a.order - b.order
    }),
    otherStages: otherStages.sort((a, b) => {
      if (a.name === b.name) return a.stageId.localeCompare(b.stageId)
      return a.name.localeCompare(b.name)
    }),
    archivedStages: archivedStages.sort((a, b) => {
      if (isNotDefined(a.order) || isNotDefined(b.order)) {
        if (a.name === b.name) return a.stageId.localeCompare(b.stageId)
        return a.name.localeCompare(b.name)
      }
      if (a.order === b.order) return a.stageId.localeCompare(b.stageId)
      return a.order - b.order
    }),
  }
}

export const isWorkflowScope = (scope: AccountStatusScope) => (s: WorkflowStageData) => s.scope === scope

export const findFinalWorkflowStage = (
  stages: WorkflowStageData[],
  scope: AccountStatusScope = 'company'
): WorkflowStageData | undefined => {
  const { pipelineStages } = toWorkflowStages(stages)
  return pipelineStages.findLast(isWorkflowScope(scope))
}

export const findLatestWorkflowStage = (stages: WorkflowHistoryItem[] = []): WorkflowHistoryItem | undefined => {
  return stages.reduce<WorkflowHistoryItem | undefined>((latest, stage) => {
    if (isNotDefined(latest)) return stage
    if (stage.timestamp > latest.timestamp) return stage
    return latest
  }, undefined)
}

export const findCurrentWorkflowStageEntry = (
  history: WorkflowHistoryItem[] = [],
  latest: WorkflowHistoryItem | undefined = findLatestWorkflowStage(history)
): WorkflowHistoryItem | undefined => {
  if (isNotDefined(latest)) return

  const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp)
  let currentEntry = latest

  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    const item = sorted[index]
    if (item.stageId !== latest.stageId) return currentEntry
    currentEntry = item
  }

  return currentEntry
}
